import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Bell, Loader2, AlertTriangle, Clock, ShieldCheck, GitBranch, FileText, X,
  RefreshCw, Bookmark, Plus, Trash2, LayoutDashboard,
  Monitor, Wifi, Server, Package, HardDrive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Notification Center ──
export function NotificationCenter({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { headers });
      setData(res.data);
    } catch { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const SEVERITY = { critical: 'bg-red-100 text-red-700 border-red-200', warning: 'bg-amber-100 text-amber-700 border-amber-200', info: 'bg-blue-100 text-blue-700 border-blue-200' };
  const TYPE_ICON = { renewal: FileText, compliance: ShieldCheck, system: AlertTriangle, rollout: GitBranch, governance: Clock };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="notification-center">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Notifications</h2>
          <p className="text-sm text-muted-foreground">{data?.total || 0} active notifications</p>
        </div>
        <div className="flex gap-2">
          {data?.critical > 0 && <Badge className="bg-red-100 text-red-700">{data.critical} critical</Badge>}
          {data?.warning > 0 && <Badge className="bg-amber-100 text-amber-700">{data.warning} warnings</Badge>}
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1 text-xs"><RefreshCw className="h-3 w-3" /> Refresh</Button>
        </div>
      </div>

      <div className="space-y-2">
        {(data?.notifications || []).length === 0 ? (
          <div className="text-center py-12 text-muted-foreground"><Bell className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">All clear — no active notifications</p></div>
        ) : (data?.notifications || []).map((n, i) => {
          const Icon = TYPE_ICON[n.type] || Bell;
          return (
            <div key={i} className={`p-3 rounded-lg border ${SEVERITY[n.severity] || SEVERITY.info}`}>
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="text-sm font-semibold">{n.title}</span>
                <Badge variant="outline" className="text-[9px] ml-auto">{n.type}</Badge>
              </div>
              <p className="text-xs mt-1 ml-6">{n.message}</p>
            </div>
          );
        })}
      </div>

      {/* Notification Preferences */}
      <NotificationPrefs token={token} />
    </div>
  );
}

function NotificationPrefs({ token }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/user/notification-preferences`, { headers })
      .then(res => setPrefs(res.data))
      .catch(() => {});
  }, [headers]);

  const updatePref = async (field, value) => {
    const updated = { ...prefs, [field]: value };
    setPrefs(updated);
    try {
      await axios.put(`${BACKEND_URL}/api/user/notification-preferences`, { [field]: value }, { headers });
    } catch { toast.error('Failed to save preference'); }
  };

  if (!prefs) return null;

  return (
    <div className="border rounded-xl p-4 space-y-3" data-testid="notification-preferences">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Notification Preferences</h3>
      <div className="space-y-2">
        {[
          { id: 'email_alerts', label: 'Email Alerts', desc: 'Receive important alerts via email' },
          { id: 'email_reports', label: 'Scheduled Report Emails', desc: 'Receive scheduled report digests' },
          { id: 'alert_contract_renewal', label: 'Contract Renewals', desc: 'Alert when contracts are approaching renewal' },
          { id: 'alert_sunset_approaching', label: 'Product Sunset Alerts', desc: 'Alert when vendor announces product sunset' },
          { id: 'alert_compliance_change', label: 'Compliance Changes', desc: 'Alert when brand compliance status changes' },
          { id: 'alert_assignment_change', label: 'Assignment Changes', desc: 'Alert when property assignments are modified' },
          { id: 'alert_new_integration', label: 'New Integrations', desc: 'Alert when new product integrations become available' },
        ].map(pref => (
          <div key={pref.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30">
            <div>
              <span className="text-sm font-medium">{pref.label}</span>
              <p className="text-[10px] text-muted-foreground">{pref.desc}</p>
            </div>
            <Switch checked={prefs[pref.id] || false} onCheckedChange={v => updatePref(pref.id, v)} />
          </div>
        ))}
      </div>
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Email Frequency</span>
          <select value={prefs.email_frequency || 'weekly'} onChange={e => updatePref('email_frequency', e.target.value)}
            className="text-xs border rounded-md px-2 py-1 bg-background">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="never">Never</option>
          </select>
        </div>
      </div>
    </div>
  );
}


// ── Saved Views ──
export function SavedViews({ token, onNavigate }) {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', view_type: 'dashboard', config: { widgets: [] } });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const WIDGET_OPTIONS = [
    { id: 'executive', label: 'Executive Summary', type: 'report' },
    { id: 'coverage', label: 'Coverage Matrix', type: 'report' },
    { id: 'notifications', label: 'Notifications', type: 'widget' },
    { id: 'status', label: 'System Status', type: 'widget' },
    { id: 'renewals', label: 'Upcoming Renewals', type: 'report' },
    { id: 'compliance', label: 'Compliance Matrix', type: 'report' },
    { id: 'lifecycle', label: 'Lifecycle Overview', type: 'report' },
    { id: 'license', label: 'License Utilization', type: 'report' },
    { id: 'rollouts', label: 'Active Rollouts', type: 'widget' },
    { id: 'supportinfo', label: 'Support Completeness', type: 'report' },
  ];

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/saved-views`, { headers });
      setViews(res.data.views || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [selectedWidgets, setSelectedWidgets] = useState(new Set());
  const toggleWidget = (id) => setSelectedWidgets(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/saved-views`, {
        name: form.name, view_type: form.view_type,
        config: { widgets: Array.from(selectedWidgets).map(id => ({ id, type: WIDGET_OPTIONS.find(w => w.id === id)?.type || 'report' })) },
      }, { headers });
      toast.success('View saved');
      setCreateOpen(false);
      setForm({ name: '', view_type: 'dashboard', config: { widgets: [] } });
      setSelectedWidgets(new Set());
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteView = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/saved-views/${id}`, { headers });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="saved-views">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Saved Views</h2>
          <p className="text-sm text-muted-foreground">Create custom dashboard layouts with the reports and widgets you use most.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="gap-1 text-xs" data-testid="create-view-btn"><Plus className="h-3 w-3" /> New View</Button>
      </div>

      {views.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><LayoutDashboard className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No saved views yet</p><p className="text-xs mt-1">Create a custom dashboard with your most-used reports</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {views.map(v => (
            <div key={v.id} className="p-4 rounded-lg border bg-card hover:bg-muted/20 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">{v.name}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteView(v.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
              </div>
              <Badge variant="outline" className="text-[9px]">{v.view_type}</Badge>
              <div className="flex flex-wrap gap-1 mt-2">
                {(v.config?.widgets || []).map((w, i) => (
                  <Badge key={i} variant="secondary" className="text-[9px]">{WIDGET_OPTIONS.find(o => o.id === w.id)?.label || w.id}</Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Saved View</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="My Executive Dashboard" className="mt-1" data-testid="view-name" /></div>
            <div>
              <label className="text-sm font-medium">Widgets ({selectedWidgets.size} selected)</label>
              <div className="mt-1 space-y-1 max-h-[250px] overflow-y-auto border rounded-lg p-2">
                {WIDGET_OPTIONS.map(w => (
                  <label key={w.id} className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/40 cursor-pointer">
                    <Checkbox checked={selectedWidgets.has(w.id)} onCheckedChange={() => toggleWidget(w.id)} />
                    <span className="text-sm">{w.label}</span>
                    <Badge variant="outline" className="text-[8px] ml-auto">{w.type}</Badge>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || selectedWidgets.size === 0}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save View</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Asset Inventory ──
export function AssetInventory({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', asset_type: 'terminal', name: '', quantity: 1, manufacturer: '', model: '' });
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterProp, setFilterProp] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const ASSET_TYPES = [
    { id: 'terminal', label: 'POS Terminal', icon: Monitor },
    { id: 'kiosk', label: 'Kiosk', icon: Monitor },
    { id: 'tablet', label: 'Tablet', icon: Monitor },
    { id: 'printer', label: 'Printer', icon: Monitor },
    { id: 'switch', label: 'Network Switch', icon: Wifi },
    { id: 'access_point', label: 'Access Point', icon: Wifi },
    { id: 'server', label: 'Server', icon: Server },
    { id: 'phone', label: 'Phone/PBX', icon: Monitor },
    { id: 'other', label: 'Other', icon: HardDrive },
  ];

  const fetchAll = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/assets`, { headers, params: filterProp ? { property_id: filterProp } : {} }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      ]);
      setData(a.data);
      setProperties(p.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers, filterProp]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assets`, form, { headers });
      toast.success('Asset added');
      setAddOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="asset-inventory">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Asset Inventory</h2>
          <p className="text-sm text-muted-foreground">Track physical hardware: terminals, kiosks, printers, network equipment.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="gap-1 text-xs" data-testid="add-asset-btn"><Plus className="h-3 w-3" /> Add Asset</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.total_assets || 0}</div>
          <div className="text-[10px] text-muted-foreground">Asset Records</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.total_quantity || 0}</div>
          <div className="text-[10px] text-muted-foreground">Total Units</div>
        </div>
        {Object.entries(data?.by_type || {}).slice(0, 2).map(([type, count]) => (
          <div key={type} className="p-3 rounded-lg border bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{count}</div>
            <div className="text-[10px] text-muted-foreground capitalize">{type.replace('_', ' ')}s</div>
          </div>
        ))}
      </div>

      <Select value={filterProp} onValueChange={v => { setFilterProp(v === '__all__' ? '' : v); setLoading(true); }}>
        <SelectTrigger className="w-64 h-8 text-sm"><SelectValue placeholder="All properties" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Properties</SelectItem>
          {properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Asset table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Name</th>
            <th className="p-2.5 text-left text-xs font-semibold">Type</th>
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-center text-xs font-semibold">Qty</th>
            <th className="p-2.5 text-left text-xs font-semibold">Manufacturer</th>
            <th className="p-2.5 text-left text-xs font-semibold">Model</th>
            <th className="p-2.5 text-left text-xs font-semibold">Location</th>
          </tr></thead>
          <tbody>
            {(data?.assets || []).map((a, i) => (
              <tr key={i} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium">{a.name}</td>
                <td className="p-2.5"><Badge variant="outline" className="text-[9px] capitalize">{a.asset_type?.replace('_', ' ')}</Badge></td>
                <td className="p-2.5 text-xs">{a.property_name}</td>
                <td className="p-2.5 text-center font-mono text-xs">{a.quantity}</td>
                <td className="p-2.5 text-xs">{a.manufacturer || '-'}</td>
                <td className="p-2.5 text-xs">{a.model || '-'}</td>
                <td className="p-2.5 text-xs text-muted-foreground">{a.location || '-'}</td>
              </tr>
            ))}
            {(data?.assets || []).length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No assets tracked yet. Add your first asset above.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Asset</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Asset Type</label>
                <Select value={form.asset_type} onValueChange={v => setForm(p => ({ ...p, asset_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium">Quantity</label><Input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} className="mt-1" /></div>
            </div>
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Front Desk POS Terminal #1" className="mt-1" data-testid="asset-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Manufacturer</label><Input value={form.manufacturer} onChange={e => setForm(p => ({ ...p, manufacturer: e.target.value }))} placeholder="Oracle/Micros" className="mt-1" /></div>
              <div><label className="text-sm font-medium">Model</label><Input value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} placeholder="Workstation 6" className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.property_id || !form.name}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Add Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Infrastructure Mapping ──
export function InfrastructureMap({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', isp_name: '', bandwidth_mbps: 0, network_vendor: '', has_server_room: false, notes: '' });
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/infrastructure`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      ]);
      setRecords(r.data.records || []);
      setProperties(p.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/infrastructure`, form, { headers });
      toast.success('Infrastructure saved');
      setEditOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="infrastructure-map">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Network & Infrastructure</h2>
          <p className="text-sm text-muted-foreground">ISP, network equipment, server rooms, and physical infrastructure per property.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1 text-xs" data-testid="add-infra-btn"><Plus className="h-3 w-3" /> Add Property Infra</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-left text-xs font-semibold">ISP</th>
            <th className="p-2.5 text-center text-xs font-semibold">Bandwidth</th>
            <th className="p-2.5 text-left text-xs font-semibold">Network</th>
            <th className="p-2.5 text-center text-xs font-semibold">Server Room</th>
            <th className="p-2.5 text-center text-xs font-semibold">UPS</th>
            <th className="p-2.5 text-left text-xs font-semibold">Notes</th>
          </tr></thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={i} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium">{r.property_name}</td>
                <td className="p-2.5 text-xs">{r.isp_name || '-'}</td>
                <td className="p-2.5 text-center font-mono text-xs">{r.bandwidth_mbps ? `${r.bandwidth_mbps} Mbps` : '-'}</td>
                <td className="p-2.5 text-xs">{r.network_vendor || '-'} {r.network_model || ''}</td>
                <td className="p-2.5 text-center">{r.has_server_room ? <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Yes</Badge> : '-'}</td>
                <td className="p-2.5 text-center">{r.ups_battery_backup ? <Badge className="text-[8px] bg-blue-100 text-blue-700">Yes</Badge> : '-'}</td>
                <td className="p-2.5 text-xs text-muted-foreground truncate max-w-[150px]">{r.notes || '-'}</td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No infrastructure data yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Property Infrastructure</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">ISP</label><Input value={form.isp_name} onChange={e => setForm(p => ({ ...p, isp_name: e.target.value }))} placeholder="Comcast Business" className="mt-1" /></div>
              <div><label className="text-sm font-medium">Bandwidth (Mbps)</label><Input type="number" value={form.bandwidth_mbps} onChange={e => setForm(p => ({ ...p, bandwidth_mbps: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
            </div>
            <div><label className="text-sm font-medium">Network Equipment</label><Input value={form.network_vendor} onChange={e => setForm(p => ({ ...p, network_vendor: e.target.value }))} placeholder="Cisco Meraki MR46" className="mt-1" /></div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.has_server_room} onCheckedChange={v => setForm(p => ({ ...p, has_server_room: v }))} /> Server Room</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.ups_battery_backup} onCheckedChange={v => setForm(p => ({ ...p, ups_battery_backup: v }))} /> UPS / Battery Backup</label>
            </div>
            <div><label className="text-sm font-medium">Notes</label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Fiber, redundant ISP..." className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.property_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
